import { useBoundStore } from '@Store/useBoundStore.ts';
import { PARTNER_FLOW_IDS_MAP } from '@Types/consts.ts';
import { OnboardingType } from '@Types/index';
import { GTM_EVENT_CATEGORY } from './consts';
import type { GTMEventLabel } from './types';

type GtagEventParams = Record<string, string | boolean | OnboardingType | GTMEventLabel>;

type BingUetCommand =
  | ['set', { pid: { em: string; ph: string } }]
  | ['event', string, Record<string, unknown>]
  | [string];

interface BingUetQueue {
  push: (...args: BingUetCommand) => void;
}

declare global {
  interface Window {
    LiliEmbeddedData: Record<string, string | boolean>[];
    LiliBingData: BingUetQueue;
    gtag: {
      (command: 'event', eventName: string, params: GtagEventParams): void;
      (command: 'set', params: Record<string, string | boolean>): void;
      (command: 'set', targetId: 'user_properties', params: Record<string, string>): void;
    };
  }
}

export const sendGtagEvent = (eventName: string, params: GtagEventParams) => {
  window?.gtag?.('event', eventName, params);
};

export const buildGA4UserProperties = () => {
  const { customer, getIsUsFlow, isMultiBusiness, getIsUboValidForNonUsFlow } =
    useBoundStore.getState();

  const uboList = customer.customerData.customerBusinessData.businessUbos?.uboResponseList;
  const hasUbo = !!uboList?.length;

  return {
    is_us_flow: getIsUsFlow() ? 'US' : 'non-US',
    entity_type: customer.customerData.customerBusinessData.mainIncomeSource || 'NA',
    has_ubo: hasUbo ? 'yes' : 'NA',
    has_ubo_non_us: getIsUboValidForNonUsFlow() ? 'yes' : 'NA',
    is_multi_business_flow: isMultiBusiness() ? 'yes' : 'NA',
  };
};

export const setGA4UserProperties = () => {
  const properties = buildGA4UserProperties();
  window?.gtag?.('set', 'user_properties', properties);
};

export const identifyGA4User = (customerId: string) => {
  if (!customerId) return;
  window?.gtag?.('set', { user_id: customerId });
};

export const dataLayerHandler = async (eventLabel: GTMEventLabel) => {
  const {
    onboardingSlice: { partnerName, isPartnerFlow },
  } = useBoundStore.getState();

  const dataLayerParams = await getDataLayerParams({
    eventLabel,
    partnerName,
    obType: isPartnerFlow ? OnboardingType.CONNECT : OnboardingType.DIRECT,
  });

  // Push all data as a single object including the event
  window.LiliEmbeddedData.push({
    event: eventLabel,
    ...dataLayerParams,
  });

  setGA4UserProperties();
  sendGtagEvent(eventLabel, dataLayerParams);
};

interface GetDataLayerPropsParams {
  eventLabel: GTMEventLabel;
  partnerName: string;
  obType: OnboardingType;
}

export const getDataLayerParams = async ({
  eventLabel,
  partnerName,
  obType,
}: GetDataLayerPropsParams) => {
  return {
    event_label: eventLabel,
    embed: useBoundStore.getState().onboardingSlice.getIsEmbedded().toString(),
    event_category: GTM_EVENT_CATEGORY,
    partner_name: partnerName || 'unknown',
    ob_type: obType,
    action: 'from code',
  } as const;
};

export const getFlowId = () => {
  const { partnerId } = useBoundStore.getState().customer;
  const partnerFlowId =
    PARTNER_FLOW_IDS_MAP?.[partnerId as keyof typeof PARTNER_FLOW_IDS_MAP] ||
    PARTNER_FLOW_IDS_MAP.CONTROL;

  return partnerFlowId;
};

export const reportBingUetConversion = (email: string, phone: string) => {
  window.LiliBingData = window.LiliBingData || [];
  window.LiliBingData.push('set', {
    pid: {
      em: email,
      ph: phone,
    },
  });
  window.LiliBingData.push('event', 'other', {});
};
